import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import AdminLoginGate from '../components/AdminLoginGate';
import AdminTabs from '../components/AdminTabs';
import { API, authHeaders, useAdminAuth } from '../lib/adminAuth';
import { ShieldOff, KeyRound, Trash2, UserPlus } from 'lucide-react';

const ROLES = [
  { key: 'superadmin', label: 'Superadmin', desc: 'Full access — manage users + toggle donations window' },
  { key: 'donations', label: 'Donations', desc: 'Can view/edit donation requests only' },
];

function CreateUserCard({ token, onCreated }) {
  const [form, setForm] = useState({ username: '', password: '', role: 'donations' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setSuccess('');
    try {
      await axios.post(`${API}/admin/users`, form, { headers: authHeaders(token) });
      setSuccess(`User '${form.username}' created. Share the password with them — they should change it after first login.`);
      setForm({ username: '', password: '', role: 'donations' });
      onCreated?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 p-6 mb-8" data-testid="admin-users-create-card">
      <div className="flex items-center gap-2 mb-4">
        <UserPlus className="w-5 h-5 text-[#F0A500]" />
        <h2 className="text-lg font-bold text-[#0A0A0A]">Create new admin user</h2>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div className="md:col-span-1">
          <Label htmlFor="new-username" className="text-xs uppercase tracking-wider font-bold text-[#52525B]">Username / email</Label>
          <Input id="new-username" data-testid="admin-users-new-username" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="donations@…" />
        </div>
        <div className="md:col-span-1">
          <Label htmlFor="new-password" className="text-xs uppercase tracking-wider font-bold text-[#52525B]">Temp password (≥10 chars)</Label>
          <Input id="new-password" data-testid="admin-users-new-password" required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••••" />
        </div>
        <div className="md:col-span-1">
          <Label htmlFor="new-role" className="text-xs uppercase tracking-wider font-bold text-[#52525B]">Role</Label>
          <select id="new-role" data-testid="admin-users-new-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#F0A500]">
            {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </div>
        <div className="md:col-span-1">
          <button type="submit" disabled={busy} data-testid="admin-users-create-btn" className="w-full px-4 py-2 bg-[#F0A500] hover:bg-[#d99400] disabled:opacity-60 text-[#0A0A0A] font-bold uppercase tracking-wider text-xs">
            {busy ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </form>
      {error && <p data-testid="admin-users-create-error" className="text-sm text-red-600 mt-3">{error}</p>}
      {success && <p data-testid="admin-users-create-success" className="text-sm text-emerald-700 mt-3 font-semibold">{success}</p>}
      <p className="text-xs text-[#52525B] mt-3 leading-relaxed">
        Roles: {ROLES.map((r) => <span key={r.key}><strong className="text-[#0A0A0A]">{r.label}</strong> — {r.desc}.</span>)}
      </p>
    </div>
  );
}

function ResetPasswordButton({ token, username, onDone }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/admin/users/${encodeURIComponent(username)}/reset-password`, { new_password: pw }, { headers: authHeaders(token) });
      setOpen(false); setPw(''); onDone?.(`Password reset for ${username}.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password.');
    } finally { setBusy(false); }
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid={`admin-users-reset-${username}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-[#52525B] hover:text-[#0A0A0A] border border-zinc-300 hover:border-zinc-400">
        <KeyRound className="w-3 h-3" /> Reset password
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="flex items-center gap-2">
      <Input data-testid={`admin-users-reset-pw-${username}`} type="text" required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (≥10)" className="h-8 text-xs w-44" />
      <button type="submit" disabled={busy} className="px-2.5 py-1 text-xs bg-[#F0A500] hover:bg-[#d99400] disabled:opacity-60 text-[#0A0A0A] font-bold uppercase tracking-wider">Save</button>
      <button type="button" onClick={() => { setOpen(false); setError(''); }} className="text-xs text-[#52525B] hover:text-[#0A0A0A]">Cancel</button>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </form>
  );
}

function Dashboard({ token, me, onLogout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/admin/users`, { headers: authHeaders(token) });
      setUsers(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else if (err.response?.status === 403) setError('Superadmin only.');
      else setError('Failed to load users.');
    } finally { setLoading(false); }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (username) => {
    if (!window.confirm(`Delete user '${username}'? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(username)}`, { headers: authHeaders(token) });
      setToast(`Deleted ${username}.`);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user.');
    }
  };

  const handleDisableTotp = async (username) => {
    if (!window.confirm(`Disable two-factor for '${username}'? They'll need to re-enrol on next login.`)) return;
    try {
      await axios.post(`${API}/admin/users/${encodeURIComponent(username)}/disable-totp`, {}, { headers: authHeaders(token) });
      setToast(`Two-factor disabled for ${username}.`);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to disable two-factor.');
    }
  };

  return (
    <div className="bg-[#F4F4F5] min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="admin-users-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-2">Admin Dashboard</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A]">Admin Users</h1>
          <p className="text-sm text-[#52525B] mt-2">Create, manage and remove admin accounts. The seeded superadmin account cannot be deleted.</p>
        </div>

        <CreateUserCard token={token} onCreated={load} />

        {toast && <p data-testid="admin-users-toast" className="text-sm text-emerald-700 mb-4 font-semibold">{toast}</p>}
        {error && <p data-testid="admin-users-error" className="text-sm text-red-600 mb-4">{error}</p>}

        {loading ? (
          <p className="text-sm text-[#52525B]">Loading…</p>
        ) : (
          <div className="bg-white border border-zinc-200" data-testid="admin-users-list">
            <div className="px-4 sm:px-6 py-3 border-b border-zinc-200 text-xs uppercase tracking-widest font-bold text-[#52525B] grid grid-cols-1 sm:grid-cols-12 gap-3">
              <div className="sm:col-span-5">User</div>
              <div className="sm:col-span-2">Role</div>
              <div className="sm:col-span-2">2FA</div>
              <div className="sm:col-span-3 text-right">Actions</div>
            </div>
            {users.map((u) => (
              <div key={u.username} data-testid={`admin-users-row-${u.username}`} className="px-4 sm:px-6 py-4 border-b border-zinc-100 last:border-0 grid grid-cols-1 sm:grid-cols-12 gap-3 items-center text-sm">
                <div className="sm:col-span-5">
                  <div className="font-semibold text-[#0A0A0A] truncate">{u.username}</div>
                  <div className="text-xs text-zinc-500">Last login: {u.last_login_at ? new Date(u.last_login_at).toLocaleString('en-GB') : '—'}</div>
                </div>
                <div className="sm:col-span-2">
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${u.role === 'superadmin' ? 'bg-[#FEF7E6] text-[#B97400]' : 'bg-zinc-100 text-[#52525B]'}`}>{u.role}</span>
                </div>
                <div className="sm:col-span-2">
                  {u.totp_enabled
                    ? <span className="text-emerald-700 font-semibold text-xs">Enabled</span>
                    : <span className="text-zinc-400 text-xs">Off</span>}
                </div>
                <div className="sm:col-span-3 flex flex-wrap items-center justify-end gap-2">
                  <ResetPasswordButton token={token} username={u.username} onDone={(m) => setToast(m)} />
                  {u.totp_enabled && (
                    <button type="button" onClick={() => handleDisableTotp(u.username)} data-testid={`admin-users-disable-totp-${u.username}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-[#52525B] hover:text-[#0A0A0A] border border-zinc-300 hover:border-zinc-400">
                      <ShieldOff className="w-3 h-3" /> Reset 2FA
                    </button>
                  )}
                  {u.username !== me.username && (
                    <button type="button" onClick={() => handleDelete(u.username)} data-testid={`admin-users-delete-${u.username}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-red-700 hover:text-white hover:bg-red-700 border border-red-300">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminUsersPage() {
  const { token, me, checking, loginSuccess, logout } = useAdminAuth();
  if (checking) return <div className="min-h-[70vh] flex items-center justify-center text-zinc-500">Checking session…</div>;
  if (!token || !me) {
    return (
      <div data-testid="admin-users-page">
        <SEO title="Admin Sign-in" description="Restricted admin area." path="/admin/users" noIndex />
        <AdminLoginGate title="Admin Sign-in" subtitle="Sign in to manage admin users." onLoggedIn={loginSuccess} />
      </div>
    );
  }
  if (me.role !== 'superadmin') {
    return (
      <div data-testid="admin-users-page">
        <SEO title="Admin Users" description="Restricted admin area." path="/admin/users" noIndex />
        <AdminTabs current="users" me={me} onLogout={logout} />
        <div className="min-h-[60vh] flex items-center justify-center text-center px-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A] mb-2">Superadmin only</h1>
            <p className="text-sm text-[#52525B]">Only superadmins can manage admin users. Ask your superadmin if you need access.</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="admin-users-page">
      <SEO title="Admin — Users" description="Restricted admin area." path="/admin/users" noIndex />
      <AdminTabs current="users" me={me} onLogout={logout} />
      <Dashboard token={token} me={me} onLogout={logout} />
    </div>
  );
}
